import {
  displayKind,
  type LibraryDisplayItem,
  type LibraryResetPlayerPayload,
  primaryBehaviour,
  resetPlayerPayloadFromLibraryItem,
} from "@healthos/client-data";
import { BEHAVIOUR_LABELS, type HealthBehaviour } from "@healthos/shared";
import {
  CARD_CHROME,
  Card,
  cn,
  MICROSTEP_BEHAVIOUR_VISUALS,
  SmoothImage,
  useReducedMotion,
} from "@healthos/ui";
import { ArrowCounterClockwise, CaretLeft, CaretRight, Play, X } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import "./contact-centre-resets.css";

const PAGE_SIZES = [
  { query: "(min-width: 1024px)", count: 4 },
  { query: "(min-width: 640px)", count: 2 },
] as const;

export function ContactCentreResetPicker({
  items,
  paused = false,
  onSelect,
  onClose,
}: {
  items: LibraryDisplayItem[];
  paused?: boolean;
  onSelect: (reset: LibraryResetPlayerPayload) => void;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const focusPageRef = useRef(false);
  const reducedMotion = useReducedMotion();
  const visibleCount = useVisibleCount();
  const [offset, setOffset] = useState(0);
  const maxOffset = Math.max(0, items.length - visibleCount);
  const pageOffset = Math.min(offset, maxOffset);
  const page = items.slice(pageOffset, pageOffset + visibleCount);
  const canPrev = pageOffset > 0;
  const canNext = pageOffset + visibleCount < items.length;
  const showPager = items.length > visibleCount;

  const shiftPage = useCallback(
    (delta: number, restoreFocus: boolean) => {
      setOffset((current) => {
        const next = Math.min(maxOffset, Math.max(0, current + delta));
        if (next === current) return current;
        const pagerDisables = (delta > 0 && next >= maxOffset) || (delta < 0 && next <= 0);
        if (restoreFocus || pagerDisables) focusPageRef.current = true;
        return next;
      });
    },
    [maxOffset],
  );

  useEffect(() => {
    setOffset((current) => Math.min(current, Math.max(0, items.length - visibleCount)));
  }, [items.length, visibleCount]);

  const bindTrack = useCallback((node: HTMLDivElement | null) => {
    if (!node || !focusPageRef.current) return;
    focusPageRef.current = false;
    node.querySelector<HTMLButtonElement>(".card-open-overlay")?.focus();
  }, []);

  useEffect(() => {
    if (!paused) closeRef.current?.focus();
  }, [paused]);

  useEffect(() => {
    if (paused) return;
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('[role="dialog"]')) return;
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        shiftPage(-visibleCount, true);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        shiftPage(visibleCount, true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, paused, shiftPage, visibleCount]);

  return (
    <section
      className="absolute inset-0 z-20"
      aria-label="Choose a Reset"
      aria-hidden={paused}
      inert={paused || undefined}
    >
      <button
        ref={closeRef}
        type="button"
        aria-label="Close Reset picker"
        onClick={onClose}
        className="absolute left-5 top-5 z-30 grid h-11 w-11 cursor-pointer place-items-center rounded-full border border-navy/10 bg-surface-ambient/80 text-ink-primary backdrop-blur-sm transition-transform duration-200 hover:scale-[1.04] active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
      >
        <X size={18} weight="bold" aria-hidden="true" />
      </button>

      <p className="pointer-events-none absolute left-1/2 top-6 z-30 m-0 -translate-x-1/2 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-ink-primary/55">
        Choose a Reset
      </p>

      {showPager ? (
        <>
          <PagerButton
            direction="previous"
            disabled={!canPrev}
            onClick={() => shiftPage(-visibleCount, false)}
          />
          <PagerButton
            direction="next"
            disabled={!canNext}
            onClick={() => shiftPage(visibleCount, false)}
          />
        </>
      ) : null}

      <div className="cc-reset-board">
        <div key={pageOffset} ref={bindTrack} className="cc-reset-track">
          {page.map((item, index) => (
            <div
              key={item.id}
              className={cn("cc-reset-stage", reducedMotion && "cc-reset-stage--static")}
              style={{ animationDelay: `${index * 70}ms` }}
            >
              <ResetCard item={item} onSelect={onSelect} priority={index < 4} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PagerButton({
  direction,
  disabled,
  onClick,
}: {
  direction: "previous" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  const previous = direction === "previous";
  return (
    <button
      type="button"
      aria-label={previous ? "Previous Resets" : "Next Resets"}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "absolute top-1/2 z-30 grid h-12 w-12 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-navy/10 bg-surface-ambient/80 text-ink-primary backdrop-blur-sm transition-transform duration-200 hover:scale-[1.04] active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:pointer-events-none disabled:opacity-30",
        previous ? "left-4 sm:left-5" : "right-4 sm:right-5",
      )}
    >
      {previous ? (
        <CaretLeft size={18} weight="bold" aria-hidden="true" />
      ) : (
        <CaretRight size={18} weight="bold" aria-hidden="true" />
      )}
    </button>
  );
}

function ResetCard({
  item,
  onSelect,
  priority,
}: {
  item: LibraryDisplayItem;
  onSelect: (reset: LibraryResetPlayerPayload) => void;
  priority: boolean;
}) {
  const payload = resetPlayerPayloadFromLibraryItem(item);
  if (!payload) return null;
  const behaviour = primaryBehaviour(item);

  return (
    <Card
      className={cn(
        CARD_CHROME,
        "group isolate relative flex min-h-[22rem] flex-col overflow-hidden border-border-strong bg-ink-heading p-0 transition-[box-shadow,transform] duration-200 ease-out hover:shadow-card-hover has-[.card-open-overlay:focus-visible]:shadow-card-hover active:scale-[0.985] lg:min-h-[360px]",
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(payload)}
        aria-label={`Play ${item.title}`}
        className="card-open-overlay absolute inset-0 z-20 cursor-pointer rounded-[inherit] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300 focus-visible:ring-inset"
      />

      {item.imageUrl ? (
        <SmoothImage
          src={item.imageUrl}
          alt=""
          priority={priority}
          frameClassName="absolute inset-0 z-0 h-full w-full"
          className="object-top transition-transform duration-200 ease-out group-hover:scale-[1.015]"
        />
      ) : (
        <div className="absolute inset-0 z-0 bg-teal-dots" />
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(to top, color-mix(in srgb, var(--color-scrim) 96%, transparent) 0%, color-mix(in srgb, var(--color-scrim) 78%, transparent) 38%, color-mix(in srgb, var(--color-scrim) 30%, transparent) 68%, transparent 100%), linear-gradient(90deg, color-mix(in srgb, var(--color-scrim) 32%, transparent) 0%, transparent 70%)",
        }}
      />

      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 z-[3] grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-ink-on-media/45 bg-ink-on-media/20 text-ink-on-media shadow-[inset_0_1px_0_color-mix(in_srgb,var(--color-surface-elevated)_50%,transparent),0_10px_28px_color-mix(in_srgb,var(--color-scrim)_28%,transparent)] backdrop-blur-md transition-transform duration-200 ease-out group-hover:scale-105"
      >
        <Play size={17} weight="fill" className="block" />
      </span>

      <div className="pointer-events-none relative z-10 mt-auto flex flex-col p-4 text-ink-on-media">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-ink-on-media">
            <ArrowCounterClockwise size={12} weight="bold" aria-hidden="true" />
            {displayKind(item.kind)}
          </span>
          {behaviour ? <BehaviourTag behaviour={behaviour} /> : null}
        </div>
        <h3 className="m-0 mt-2.5 font-sans text-2xl font-medium leading-tight tracking-tight text-ink-on-media">
          {item.title}
        </h3>
      </div>
    </Card>
  );
}

function BehaviourTag({ behaviour }: { behaviour: HealthBehaviour }) {
  const visual = MICROSTEP_BEHAVIOUR_VISUALS[behaviour];
  if (!visual) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-ink-on-media/30 bg-scrim/35 px-2 py-0.5 font-mono text-2xs font-semibold uppercase tracking-[0.12em] text-ink-on-media">
      <visual.Icon size={11} weight="duotone" aria-hidden="true" color={visual.color} />
      {BEHAVIOUR_LABELS[behaviour]}
    </span>
  );
}

export function visibleResetCount(): number {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return 4;
  const match = PAGE_SIZES.find((size) => window.matchMedia(size.query).matches);
  return match?.count ?? 1;
}

function useVisibleCount(): number {
  const [count, setCount] = useState(visibleResetCount);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const queries = PAGE_SIZES.map((size) => window.matchMedia(size.query));
    const update = () => setCount(visibleResetCount());
    for (const query of queries) query.addEventListener("change", update);
    return () => {
      for (const query of queries) query.removeEventListener("change", update);
    };
  }, []);

  return count;
}
